import { ServiceError } from '../errors'
import { pmsUrl, postJson } from '../http'
import type { StayRef } from '../types'

/**
 * `getreservependingtopaid_api` (E2), copiado de src/api/getreservependingtopaid_api/ de la app actual.
 * Lo usan `pagos` y `fianza`; una misma pantalla suele pedir ambos a la vez, así que la respuesta se
 * comparte durante unos segundos para no llamar dos veces al PMS.
 */
export interface ApiPendingToPaid {
  ok: boolean
  valid?: boolean
  message?: string
  error?: string
  status?: string
  totalPrice: string
  pendingPrice: string
  freeCancelLimitDate?: string
  freeCancel: boolean
  bailPaid: boolean
  bailToRefundPrice?: string
  bailRefund: boolean
  bailRefundDate?: string
  bailPrice?: string
  bailPaymentDate?: string
  paidTypeBailName?: string
  advancePaid: boolean
  advancePrice?: string
  advancePaymentDate?: string
  restPaid: boolean
  restPaymentDate?: string
  restPrice?: string
  penalizePrice?: string
  paidTypeRestId?: string
  paidTypeBailId?: string
  paidTypeAdvanceId?: string
  paidTypeStandardId: string
  paymentTypeCardId: string
  paymentTypeBizumId: string
  rateConditions?: string
  validCardId?: string
  cardNumber?: string
  cardExpirationDate?: string
  cardName?: string
  reserveChannelIdentifier?: string
  emptyCVV?: boolean
  advancePercent?: string
}

const SHARE_MS = 3000
const inflight = new Map<string, { at: number; promise: Promise<ApiPendingToPaid> }>()

/** La petición compartida no usa la señal de nadie: cada llamante deja de esperar con la suya. */
function withSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise
  if (signal.aborted) return Promise.reject(new ServiceError('server', 'aborted'))
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new ServiceError('server', 'aborted'))
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort)
        reject(error instanceof Error ? error : new ServiceError('server', String(error)))
      },
    )
  })
}

export function fetchPendingToPaid(stay: StayRef, lang: string, signal?: AbortSignal): Promise<ApiPendingToPaid> {
  const key = `${stay.serverPath}/${stay.id}/${lang}`
  const cached = inflight.get(key)
  if (cached && Date.now() - cached.at < SHARE_MS) return withSignal(cached.promise, signal)
  // Sin la señal del primer llamante: si ese se desmonta (p. ej. StrictMode), el segundo no hereda el aborto.
  const promise = postJson<ApiPendingToPaid>(pmsUrl(stay.serverPath, 'getreservependingtopaid_api'), { reserveId: stay.id, userLang: lang }, { auth: 'partner' })
  inflight.set(key, { at: Date.now(), promise })
  promise.catch(() => inflight.delete(key))
  return withSignal(promise, signal)
}

export function clearPendingToPaidCache(): void {
  inflight.clear()
}

/** Fechas de E2: `dd-MM-yyyy` o `dd-MM-yyyy HH:mm` → ISO local; cualquier otra cosa se devuelve tal cual. */
export function pmsDateToIso(value: string | undefined | null): string | null {
  if (!value || value.trim() === '') return null
  const m = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2}))?/.exec(value.trim())
  if (!m) return value
  const [, d, mo, y, hh = '00', mm = '00'] = m
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm))
  const pad = (n: number) => String(n).padStart(2, '0')
  const offset = -dt.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const abs = Math.abs(offset)
  return `${y}-${mo}-${d}T${hh}:${mm}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}
